import { CompletionMessage } from '@app-libs/model';
import { reduce, Subject, takeUntil } from 'rxjs';
import { CompletionApiService } from '../../../services/api/completion-api.service';
import { GeminiApiService } from '../../../services/api/gemini-api.service';
import { OpenaiAPIService } from '../../../services/api/openai-api.service';
import { TestControl } from '../../models/test-control.interface';

/**
 * Factory function to create AI test definitions with proper service injection
 */
export function createAITestDefinitions(
  openaiApiService: OpenaiAPIService,
  geminiApiService: GeminiApiService,
  completionApiService: CompletionApiService,
  destroy$: Subject<void>
): TestControl[] {

  return [
    {
      id: 'stream-completion',
      title: 'Gemini Completion',
      description: 'Test streaming AI completion with Gemini model',
      category: 'ai-tests',
      type: 'button',
      icon: 'stream',
      execute: () => streamGemini(geminiApiService, destroy$)
    },
    {
      id: 'stream-openai',
      title: 'Stream OpenAI',
      description: 'Test streaming completion with OpenAI GPT-4',
      category: 'ai-tests',
      type: 'button',
      icon: 'smart_toy',
      execute: () => streamOpenAI(openaiApiService, destroy$)
    },
    {
      id: 'stream-completion-api',
      title: 'Stream Completion API',
      description: 'Test streaming completion with Completion API',
      category: 'ai-tests',
      type: 'button',
      icon: 'smart_toy',
      execute: () => streamCompletion(completionApiService, "gemini-2.5-flash", destroy$)
    },
    {
      id: 'function-calling',
      title: 'Function Calling',
      description: 'Test AI function calling capabilities',
      category: 'ai-tests',
      type: 'button',
      icon: 'functions',
      execute: () => functionCalling(openaiApiService, destroy$)
    },
    {
      id: 'create-summary',
      title: 'Create Summary',
      description: 'Generate conversation summary title',
      category: 'ai-tests',
      type: 'button',
      icon: 'summarize',
      execute: () => createSummaryTitle(openaiApiService, destroy$)
    },
    {
      id: 'test-with-files',
      title: 'Test with Files',
      description: 'Test completion with file attachments',
      category: 'ai-tests',
      type: 'button',
      icon: 'attach_file',
      execute: () => testWithFiles()
    },
    {
      id: 'create-error',
      title: 'Create Error',
      description: 'Test error handling by throwing an error',
      category: 'ai-tests',
      type: 'button',
      icon: 'error',
      execute: () => createError()
    }
  ];
}

/**
 * Stream completion test (migrated from invokeStreamCompletion)
 */
async function streamGemini(
  apiService: GeminiApiService,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Starting stream completion test...');

  return new Promise((resolve, reject) => {
    apiService
      .createCompletion({
        messages: [{ role: 'user', content: 'what is your role?' }],
        workspaceId: "123",
        model: "gemini-2.5-flash",
        stream: true
      })
      .pipe(
        reduce((acc, chunk) => acc + chunk, ''),
        takeUntil(destroy$)
      )
      .subscribe({
        next: (final_message) => {
          console.log('Stream completion result:', final_message);
          console.log(JSON.stringify(final_message, null, 2));
          resolve();
        },
        error: (error) => {
          console.error('Stream completion error:', error);
          reject(error);
        },
        complete: () => {
          console.log('Stream completion completed');
        }
      });
  });
}

/**
 * Stream OpenAI test (migrated from streamOpenAI)
 */
async function streamOpenAI(
  openaiApiService: OpenaiAPIService,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Starting OpenAI stream test...');

  return new Promise((resolve, reject) => {
    openaiApiService
      .createCompletion({
        messages: [{ role: 'user', content: 'what is your name?' }],
        stream: true,
        model: "gpt-4o",
        workspaceId: "123"
      })
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response) => {
          console.log('OpenAI stream chunk:', response);
        },
        error: (error) => {
          console.error('OpenAI stream error:', error);
          reject(error);
        },
        complete: () => {
          console.log('OpenAI stream completed');
          resolve();
        }
      });
  });
}

/**
 * Stream OpenAI test (migrated from streamOpenAI)
 */
async function streamCompletion(
  completionApiService: CompletionApiService,
  model = "gpt-4o",
  destroy$: Subject<void>
): Promise<void> {
  console.log('Starting Completion API stream test...');

  return new Promise((resolve, reject) => {
    completionApiService
      .createCompletion({
        messages: [{ role: 'user', content: 'what is your name?' }],
        stream: true,
        model: model,
        workspaceId: "123"
      })
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response) => {
          console.log('completionApiService stream chunk:', response);
        },
        error: (error) => {
          console.error('completionApiService stream error:', error);
          reject(error);
        },
        complete: () => {
          console.log('completionApiService stream completed');
          resolve();
        }
      });
  });
}

/**
 * Function calling test (migrated from functionCalling)
 */
async function functionCalling(
  openaiApiService: OpenaiAPIService,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Starting function calling test...');

  const messages = [
    {
      role: 'system',
      content: `
        Persona:
        You are a friendly and supportive tutor who helps students achieve their study goals.
        
        Function Calling and Data Capture:
        Your secondary role is to capture any new personal or study-related information the student shares.
        
        **Important**:
        - Always respond to the user's query with a visible and meaningful message.
        - Ensure 'user_attributes' contains meaningful key-value pairs and is never empty.
        - When creating mock exams, explain your actions to the user.
      `
    },
    {
      role: 'user',
      content: 'Create a mock exam for me, 1 question only. I love science, and I\'m aiming to get an A in physics this year. I\'m in grade 8 and 13 years old.'
    }
  ];

  return new Promise((resolve, reject) => {
    openaiApiService
      .createCompletion({
        messages: messages,
        stream: false,
        model: 'gpt-4o',
        workspaceId: "123"
      })
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response) => {
          console.log('Function calling result:', response);

          const choice = response.choices[0];
          if (choice.finish_reason === 'function_call') {
            const functionCall = choice.message.function_call;
            console.log('Function call received:', functionCall);
            console.log('Function arguments:', JSON.parse(functionCall.arguments || '{}'));
          } else {
            console.log('Assistant Response:', choice.message.content);
          }

          resolve();
        },
        error: (error) => {
          console.error('Function calling error:', error);
          reject(error);
        }
      });
  });
}

/**
 * Create summary title test (migrated from createSummaryTitle)
 */
async function createSummaryTitle(
  openaiApiService: OpenaiAPIService,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Starting summary title creation test...');

  const messages: CompletionMessage[] = [
    { role: 'user', content: 'Hi' } as CompletionMessage,
    { role: 'assistant', content: 'hi how are you' } as CompletionMessage,
    { role: 'user', content: 'my name is john' } as CompletionMessage,
    { role: 'assistant', content: 'my name is study buddy' } as CompletionMessage
  ];

  return new Promise((resolve, reject) => {
    openaiApiService
      .createCompletionAsText(
        'Generate a clear, descriptive title (max 70 characters) that summarizes the conversation.',
        messages,
        'gpt-4o-mini'
      )
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response) => {
          console.log('Summary title result:', response);
          resolve();
        },
        error: (error) => {
          console.error('Summary title error:', error);
          reject(error);
        },
        complete: () => {
          console.log('Summary title completed');
        }
      });
  });
}

/**
 * Test with files (migrated from invokeStreamCompletionWithFiles)
 */
async function testWithFiles(): Promise<void> {
  console.log('Starting test with files...');

  // This would be implemented based on your file handling logic
  // For now, it's a placeholder
  console.log('File handling test - placeholder implementation');

  return Promise.resolve();
}

/**
 * Create error test (migrated from createError)
 */
async function createError(): Promise<void> {
  console.log('Creating intentional error...');
  throw new Error('Test Error - This is intentional for testing error handling');
}